import { Box } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

import { FORTA_SUCCESS } from '../../utils/colors';

type ArrowDirection = 'left' | 'right' | 'up' | 'down';

interface AnimatedArrowProps {
  direction?: ArrowDirection;
  color?: string;
  width?: number;
  height?: number;
  strokeWidth?: number;
  className?: string;
}

const dash = keyframes`
  0% {
    stroke-dashoffset: 24;
  }
  100% {
    stroke-dashoffset: 0;
  }
`;

const move = keyframes`
  0% {
    transform: translateX(-8px);
    opacity: 0;
  }
  30% {
    opacity: 1;
  }
  70% {
    opacity: 1;
  }
  100% {
    transform: translateX(8px);
    opacity: 0;
  }
`;

const Container = styled(Box)<{ $direction: ArrowDirection }>(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 0,
}));

const Svg = styled('svg')<{
  $color: string;
  $direction: ArrowDirection;
}>(({ $color, $direction }) => ({
  overflow: 'visible',
  transform:
    $direction === 'left'
      ? 'rotate(180deg)'
      : $direction === 'up'
        ? 'rotate(-90deg)'
        : $direction === 'down'
          ? 'rotate(90deg)'
          : 'none',
  '& .fh-arrow-line': {
    stroke: $color,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    fill: 'none',
    strokeDasharray: '6 6',
    animation: `${dash} 1.2s linear infinite`,
  },
  '& .fh-arrow-head': {
    fill: $color,
  },
  '& .fh-arrow-glow': {
    fill: $color,
    opacity: 0.2,
    animation: `${move} 1.2s ease-in-out infinite`,
  },
}));

function AnimatedArrow({
  direction = 'right',
  color = FORTA_SUCCESS,
  width = 120,
  height = 24,
  strokeWidth = 2,
  className,
}: AnimatedArrowProps) {
  const lineY = height / 2;
  const headSize = Math.min(8, Math.max(6, Math.round(height / 3)));
  const lineStart = 2;
  const lineEnd = width - headSize - 2;

  return (
    <Container $direction={direction} className={className}>
      <Svg
        $color={color}
        $direction={direction}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="animated arrow"
      >
        <defs>
          <filter id="fh-arrow-blur" x="-10%" y="-50%" width="120%" height="200%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="2" />
          </filter>
        </defs>
        <g>
          <line
            className="fh-arrow-line"
            x1={lineStart}
            y1={lineY}
            x2={lineEnd}
            y2={lineY}
            strokeWidth={strokeWidth}
          />
          <polygon
            className="fh-arrow-head"
            points={`${lineEnd},${lineY - headSize / 2} ${lineEnd},${lineY + headSize / 2} ${
              width - 2
            },${lineY}`}
          />
          <polygon
            className="fh-arrow-glow"
            filter="url(#fh-arrow-blur)"
            points={`${lineEnd - headSize},${lineY - headSize / 3} ${lineEnd - headSize},${
              lineY + headSize / 3
            } ${lineEnd},${lineY}`}
          />
        </g>
      </Svg>
    </Container>
  );
}

export default AnimatedArrow;
